import i18next, { InitOptions } from "i18next";

// Import JSON (use `assert { type: "json" }` in Vite/modern bundlers if needed)
import en from "./en.json";
import hu from "./hu.json";
import secureLocalStorage from "react-secure-storage";

// 1) Supported languages
export const SUPPORTED_LANGS = ["en", "hu"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

// 2) Build typed resources
const resources = {
    en: { translation: en },
    hu: { translation: hu }
} as const;

// 3) Create a Resource type for module augmentation
type Resource = (typeof resources)["en"];

// 4) Augment i18next types so `t()` is key-safe
declare module "i18next" {
    interface CustomTypeOptions {
        defaultNS: "translation";
        resources: Resource;
        // Interpolation values typing (optional)
        returnNull: false;
    }
}

const LANG_KEY = "language";

const options: InitOptions = {
    resources,
    lng: "hu", // temporary initial; will be overridden by parent
    fallbackLng: "hu",
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    // helpful for missing keys during dev
    saveMissing: false,
    returnNull: false
};

// Call once during app bootstrap
const i18n = i18next.createInstance();
export async function initI18n(initialLang: SupportedLang = "hu") {
    await i18n.init({ ...options, lng: initialLang });
    return i18n;
}

// Runtime guard
function isSupportedLang(x: string): x is SupportedLang {
    return (SUPPORTED_LANGS as readonly string[]).includes(x);
}

// Helpers
export function getStoredLang(): SupportedLang {
    const stored = secureLocalStorage.getItem(LANG_KEY);
    return isSupportedLang(stored as string) ? (stored as SupportedLang) : "hu";
}

export function setStoredLang(lang: SupportedLang) {
    secureLocalStorage.setItem(LANG_KEY, lang);
}
